import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchNotifications,
  fetchUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/api/notifications/notificationsApi';
import type { NotificationPage } from '@/api/notifications/notificationsApi.types';
import {
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
} from '@/constants/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import { LIVE_STATUS_REFETCH_INTERVAL_MILLISECONDS } from '@/constants/liveStatus';

const NOTIFICATION_DROPDOWN_PAGE_SIZE = 10;
const NOTIFICATION_CENTER_PAGE_SIZE = 20;

export function useNotificationsQuery(): UseQueryResult<NotificationPage, Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: function loadFirstPage() {
      return fetchNotifications(1, NOTIFICATION_DROPDOWN_PAGE_SIZE);
    },
    enabled: status === 'authenticated',
  });
}

export function useNotificationsPageQuery(
  page: number,
  unreadOnly: boolean,
  category: string | null = null
): UseQueryResult<NotificationPage, Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, 'page', page, unreadOnly, category],
    queryFn: function loadPage() {
      return fetchNotifications(page, NOTIFICATION_CENTER_PAGE_SIZE, unreadOnly, category);
    },
    enabled: status === 'authenticated',
    placeholderData: keepPreviousData,
  });
}

export function useUnreadNotificationCountQuery(): UseQueryResult<number, Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
    queryFn: fetchUnreadNotificationCount,
    enabled: status === 'authenticated',
    refetchInterval: LIVE_STATUS_REFETCH_INTERVAL_MILLISECONDS,
    refetchOnWindowFocus: true,
  });
}

export const NOTIFICATION_CENTER_PAGE_SIZE_VALUE = NOTIFICATION_CENTER_PAGE_SIZE;

function invalidateNotificationQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY });
}

export function useMarkNotificationReadMutation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: function refresh() {
      invalidateNotificationQueries(queryClient);
    },
  });
}

export function useMarkAllNotificationsReadMutation(): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: function refresh() {
      invalidateNotificationQueries(queryClient);
    },
  });
}
